import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

export type AdminTenant = {
  id: string;
  name: string;
  status: string;
  subscriptionStatus: string;
  trialExpiresAt: string | null;
  createdAt: string | null;
};

export type PortalQueueItem = {
  id: string;
  status: string;
  clientId: string | null;
  fullName: string;
  email: string;
  riskLevel: string;
  riskFlags: string[];
  submittedAt: string | null;
};

export type PortalTokenItem = {
  id: string;
  tokenType: string;
  status: string;
  contactEmail: string;
  contactName: string;
  portalPath: string;
  updatedAt: string | null;
};

export type SupportTicketItem = {
  id: string;
  status: string;
  priority: string;
  title: string;
  reasonCode: string;
  updatedAt: string | null;
};

export type TrialInviteItem = {
  id: string;
  email: string;
  status: string;
  expiresAt: string | null;
  createdAt: string | null;
};

export type ControlTowerDiagnostic = {
  code: "CONTROL_TOWER_INDEX_MISSING" | "CONTROL_TOWER_OPTIONAL_FEED_FAILED" | string;
  message: string;
  collection?: string;
  indexUrl?: string | null;
  severity?: "warning" | "error";
};

export type TenantSnapshot = {
  firm: {
    id: string;
    name: string;
    status: string;
    subscriptionStatus: string;
    trialExpiresAt: string | null;
    memberCount: number;
  };
  profile: Record<string, unknown>;
  retention: Record<string, unknown>;
  email: Record<string, unknown>;
  knowledge: Record<string, unknown>;
  intelligence: Record<string, unknown>;
  members: Array<Record<string, unknown> & { id: string }>;
  integrations: Array<Record<string, unknown> & { id: string }>;
  supportTickets: SupportTicketItem[];
  trialInvites: TrialInviteItem[];
  portalQueue: PortalQueueItem[];
  portalTokens: PortalTokenItem[];
  auditTrail: Array<{ id: string; action: string; actorUid: string; createdAt: string | null }>;
  diagnostics?: ControlTowerDiagnostic[];
};

export type ControlTowerTelemetryPayload = {
  jobs: Array<{ id: string; jobName: string; status: string; docCount: number; createdAt: string | null; completedAt: string | null }>;
  events: Array<{ id: string; firmId: string; model: string; effectiveModel?: string; routingReason?: string; action: string; latencyMs: number; totalTokens: number; timestamp: string | null }>;
  uxEvents: Array<{ id: string; eventName: string; routeKey: string; surface: string; outcome?: string | null; timestamp: string | null }>;
  alerts: Array<{ id: string; title?: string; body?: string; severity?: string; createdAt?: string | null }>;
  diagnostics?: ControlTowerDiagnostic[];
};

export type ControlTowerFinancialsPayload = {
  rows: Array<{ id: string; firmName: string; stripeAmount: number; creditSales: number; apiCost: number; balance: number; burnRate7d: number }>;
  diagnostics?: ControlTowerDiagnostic[];
};

export type TenantDataExportResult = {
  success: boolean;
  jobId: string;
  downloadUrl: string;
  expiresAtIso: string;
  summary: Record<string, number>;
};

export type TenantDeletionResult = {
  success: boolean;
  targetFirmId: string;
  deletedCollections: Record<string, number>;
  preservedCollections: Record<string, number>;
  storageFilesDeleted: number;
  usersAnonymized: number;
  manifestId: string;
};

export const adminListTenantsCallable = httpsCallable<
  { query?: string; limit?: number },
  { tenants: AdminTenant[] }
>(functions, "adminListTenants");

export const adminGetTenantSettingsSnapshotCallable = httpsCallable<
  { targetFirmId: string },
  TenantSnapshot
>(functions, "adminGetTenantSettingsSnapshot");

export const adminGetControlTowerSectionCallable = httpsCallable<
  { targetFirmId: string; section: string },
  Partial<TenantSnapshot>
>(functions, "adminGetControlTowerSection");

export const adminGetControlTowerHealthCallable = httpsCallable<
  { targetFirmId?: string },
  {
    ok: boolean;
    databaseId: string;
    region: string;
    targetFirmId: string | null;
    callableExports: string[];
    checks: Array<{ id: string; status: "pass" | "warn" | "fail"; code?: string; message: string; collection?: string; indexUrl?: string | null }>;
  }
>(functions, "adminGetControlTowerHealth");

export const adminGetControlTowerTelemetryCallable = httpsCallable<
  Record<string, never>,
  ControlTowerTelemetryPayload
>(functions, "adminGetControlTowerTelemetry");

export const adminGetControlTowerFinancialsCallable = httpsCallable<
  Record<string, never>,
  ControlTowerFinancialsPayload
>(functions, "adminGetControlTowerFinancials");

export const adminMutateTenantSubscriptionCallable = httpsCallable<
  { targetFirmId: string; action: string; reasonCode?: string },
  { success: boolean; targetFirmId: string; action: string; status: string; subscriptionStatus: string; trialExpiresAt: string | null }
>(functions, "adminMutateTenantSubscription");

export const adminCreateTenantTrialInviteCallable = httpsCallable<
  { targetFirmId: string; email: string; reasonCode?: string },
  { success: boolean; inviteId: string; status: string; expiresAt: string }
>(functions, "adminCreateTenantTrialInvite");

export const adminUpsertTenantSupportTicketCallable = httpsCallable<
  { targetFirmId: string; ticketId?: string; title: string; details?: string; priority?: string; action: string; reasonCode?: string },
  { success: boolean; ticketId: string; status: string }
>(functions, "adminUpsertTenantSupportTicket");

export const adminMutateTenantMemberCallable = httpsCallable<
  { targetFirmId: string; targetUid: string; action: string; nextRole?: string; reasonCode?: string },
  { success: boolean; targetUid: string; action: string }
>(functions, "adminMutateTenantMember");

export const adminIssueTenantPortalTokenCallable = httpsCallable<
  { targetFirmId: string; tokenType: string; clientId?: string; contactEmail: string; contactName: string; contactRole?: string; reasonCode?: string },
  { success: boolean; tokenId: string; portalPath: string; expiresAt: string }
>(functions, "adminIssueTenantPortalToken");

export const adminReviewTenantOnboardingSubmissionCallable = httpsCallable<
  { targetFirmId: string; submissionId: string; action: string; reviewNotes?: string; reasonCode?: string },
  { success: boolean; submissionId: string; status: string }
>(functions, "adminReviewTenantOnboardingSubmission");

export const generateTenantDataExportCallable = httpsCallable<
  { targetFirmId: string; reasonCode?: string },
  TenantDataExportResult
>(functions, "generateTenantDataExport");

export const executeTenantDeletionCallable = httpsCallable<
  { targetFirmId: string; confirmationFirmName: string; reasonCode?: string },
  TenantDeletionResult
>(functions, "executeTenantDeletion");

export const adminGetOperatorAccessPolicyCallable = httpsCallable<
  Record<string, never>,
  { nonOwnerKillSwitch: boolean; operatorSessionTimeoutMinutes: number; mandatoryMfa: boolean; updatedAt: string | null; updatedByUid: string | null }
>(functions, "adminGetOperatorAccessPolicy");

export const adminSetOperatorAccessPolicyCallable = httpsCallable<
  { nonOwnerKillSwitch: boolean; operatorSessionTimeoutMinutes: number; mandatoryMfa: boolean; reasonCode?: string },
  { success: boolean; nonOwnerKillSwitch: boolean; operatorSessionTimeoutMinutes: number; mandatoryMfa: boolean }
>(functions, "adminSetOperatorAccessPolicy");

export const adminCreateOperatorAccountCallable = httpsCallable<
  { email: string; platformRole: string; displayName?: string; reasonCode?: string },
  { success: boolean; uid: string; email: string; platformRole: string }
>(functions, "adminCreateOperatorAccount");

export const adminGetCommercialSettingsCallable = httpsCallable<
  Record<string, never>,
  {
    planBasePriceAed: number;
    maxDiscountPercent: number;
    defaultMonthlyIncludedCredits: number;
    defaultCreditCap: number;
    blendedAiCostPerCreditAed: number;
    foundingSlotLimit: number;
    updatedAt: string | null;
    updatedByUid: string | null;
  }
>(functions, "adminGetCommercialSettings");

export const adminInviteNewTenantCallable = httpsCallable<
  {
    firmName: string;
    adminEmail: string;
    jurisdiction?: string;
    trialDays?: number;
    reasonCode?: string;
    inviteUrlOrigin?: string;
  },
  {
    success: boolean;
    firmId: string;
    inviteId: string;
    inviteUrl?: string;
    loginUrl?: string;
    authProvisioningStatus?: string;
    authProvisioningError?: string | null;
    passwordSetupLinkError?: string | null;
    deliveryStatus?: "sent" | "failed";
    deliveryError?: string | null;
    expiresAt: string;
  }
>(functions, "adminInviteNewTenant");

export const acceptTenantInviteByTokenCallable = httpsCallable<
  { inviteToken: string },
  {
    success: boolean;
    firmId: string;
    firmName: string;
    adminEmail: string;
    status: string;
    trialExpiresAt: string | null;
    passwordSetupLink: string | null;
    loginUrl: string;
    authProvisioningStatus: string | null;
  }
>(functions, "acceptTenantInviteByToken");

export const adminUpsertCommercialSettingsCallable = httpsCallable<
  {
    planBasePriceAed: number;
    maxDiscountPercent: number;
    defaultMonthlyIncludedCredits: number;
    defaultCreditCap: number;
    blendedAiCostPerCreditAed: number;
    foundingSlotLimit: number;
    reasonCode?: string;
  },
  {
    success: boolean;
    planBasePriceAed: number;
    maxDiscountPercent: number;
    defaultMonthlyIncludedCredits: number;
    defaultCreditCap: number;
    blendedAiCostPerCreditAed: number;
    foundingSlotLimit: number;
    updatedByUid: string;
  }
>(functions, "adminUpsertCommercialSettings");
